import type { Locale } from "../i18n/index.js";

/**
 * Cultivator skins. The full roster matches the design doc / protocol; only
 * skins with shipped art appear in {@link SKIN_DEFS} and are selectable. New
 * skins are added by dropping their art and appending a def here (and, for
 * SKIN-3, a `SkinIdSchema` entry). All sprites are treated as spritesheets —
 * existing walk cycles have 5 frames, future static top-downs have 1.
 */
export const SKIN_IDS = [
  "qingyun",
  "moyu",
  "danxia",
  "xuanshuang",
  "zifu",
  "linglong",
] as const;

export type SkinId = (typeof SKIN_IDS)[number];

export const DEFAULT_SKIN_ID: SkinId = "qingyun";

export interface SkinDef {
  id: SkinId;
  sheetKey: string;
  moveAnimationKey: string;
  boostAnimationKey: string;
  /** Top-down spritesheet path (used in game + as the picker thumbnail). */
  path: string;
  /** Number of horizontal frames in the sheet (1 = static). */
  frameCount: number;
  /** Optional side portrait for the picker. */
  portraitPath?: string;
  label: Record<Locale, string>;
  swatchColor: string;
}

function def(
  id: SkinId,
  path: string,
  frameCount: number,
  label: Record<Locale, string>,
  swatchColor: string,
  portraitPath?: string,
): SkinDef {
  return {
    id,
    sheetKey: `topdown-cultivator-${id}`,
    moveAnimationKey: `topdown-cultivator-${id}-move`,
    boostAnimationKey: `topdown-cultivator-${id}-boost`,
    path,
    frameCount,
    portraitPath,
    label,
    swatchColor,
  };
}

/**
 * Skins with shipped art (selectable now). The two existing walk-cycle sheets
 * become qingyun / moyu; the rest of the roster lands with SKIN-4 art.
 */
export const SKIN_DEFS: readonly SkinDef[] = [
  def(
    "qingyun",
    "/characters/qingyun/qingyun-topdown.png",
    5,
    { "zh-CN": "青云", en: "Qingyun" },
    "#9ce6ff",
    "/characters/qingyun/qingyun-portrait.png",
  ),
  def(
    "moyu",
    "/characters/moyu/moyu-topdown.png",
    5,
    { "zh-CN": "墨羽", en: "Moyu" },
    "#c9d6e8",
    "/characters/moyu/moyu-portrait.png",
  ),
  def(
    "danxia",
    "/characters/danxia/danxia-topdown.png",
    5,
    { "zh-CN": "丹霞", en: "Danxia" },
    "#f5a15a",
    "/characters/danxia/danxia-portrait.png",
  ),
  def(
    "xuanshuang",
    "/characters/xuanshuang/xuanshuang-topdown.png",
    5,
    { "zh-CN": "玄霜", en: "Xuanshuang" },
    "#bfe6ff",
    "/characters/xuanshuang/xuanshuang-portrait.png",
  ),
  def(
    "zifu",
    "/characters/zifu/zifu-topdown.png",
    5,
    { "zh-CN": "紫府", en: "Zifu" },
    "#b78cff",
    "/characters/zifu/zifu-portrait.png",
  ),
  def(
    "linglong",
    "/characters/linglong/linglong-topdown.png",
    5,
    { "zh-CN": "玲珑", en: "Linglong" },
    "#ffb066",
    "/characters/linglong/linglong-portrait.png",
  ),
];

const SKIN_BY_ID = new Map<SkinId, SkinDef>(SKIN_DEFS.map((d) => [d.id, d]));

export function isAvailableSkinId(value: unknown): value is SkinId {
  return typeof value === "string" && SKIN_BY_ID.has(value as SkinId);
}

/** Returns a usable (shipped) skin id, falling back to the default. */
export function normalizeSkinId(value: unknown): SkinId {
  return isAvailableSkinId(value) ? value : DEFAULT_SKIN_ID;
}

export function getSkinDef(id: SkinId): SkinDef | undefined {
  return SKIN_BY_ID.get(id);
}

/**
 * Resolve which skin to render. A chosen, shipped skin wins; otherwise fall
 * back to a deterministic hash over the seed (current behaviour for players
 * whose skin is unknown / not chosen).
 */
export function resolveSkinDef(
  chosen: SkinId | null | undefined,
  hashSeed: number,
): SkinDef {
  if (chosen && SKIN_BY_ID.has(chosen)) {
    return SKIN_BY_ID.get(chosen)!;
  }
  const index = Math.abs(hashSeed) % SKIN_DEFS.length;
  return SKIN_DEFS[index] ?? SKIN_DEFS[0]!;
}
